// API for the shop clock dashboards (PCW at /pcw, the Mill at /mill-shop —
// see shopClocks.js) — hours summaries, the punch log, worker/category/custom
// job management, and the job schedule. Every endpoint is admin-only; each
// shop has the same endpoints under its own base (/api/pcw, /api/mill).
const jsonRequest = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    // Merged rather than replaced so callers can add headers (every call here
    // adds x-user-id) without dropping the JSON content type.
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

// The shop clock endpoints identify the caller by this header (no token validation
// server-side — the same trust model the feature request admin routes use),
// so every call needs the signed-in user's id.
const adminRequest = (url, userId, options = {}) =>
  jsonRequest(url, {
    ...options,
    headers: { "x-user-id": userId || "" },
  });

// Builds ?from=&to=&... from an object, skipping empty values.
const queryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// "185" minutes -> "3h 5m"; keeps tables compact and consistent.
export const formatMinutes = (minutes) => {
  const total = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins}m`;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
};

// ---- Jobs ----

// A punch is against a Bistrack works order (worksOrderNumber) or a custom
// job a worker created on the shop tablet (workOrder.custom, the name in
// workOrder.description, no number). Both have a string key — "138447" or
// "c-<custom job id>" — used for ?wo= links and the API's workOrder param.
// Mirrors server/routes/api/PCW/pcwJobs.js.
export const jobKeyOf = (workOrder) => {
  if (!workOrder) return null;
  if (workOrder.custom) return `c-${workOrder.customWorkOrderId || ""}`;
  return workOrder.worksOrderNumber != null
    ? String(workOrder.worksOrderNumber)
    : null;
};

export const isJobKey = (value) =>
  /^\d{1,12}$/.test(value || "") || /^c-[0-9a-f]{24}$/i.test(value || "");

// How a job reads in tables: { title, detail, custom }. A works order leads
// with its number ("WO 138447") and its Bistrack name ("Day Kitchen") as the
// detail; a custom job leads with its name.
export const jobLabel = (workOrder) => {
  const wo = workOrder || {};
  const description = (wo.description || "").trim();
  if (wo.custom)
    return { title: description || "Custom job", detail: "", custom: true };
  return {
    title:
      wo.worksOrderNumber != null ? `WO ${wo.worksOrderNumber}` : "Unknown job",
    detail: description,
    custom: false,
  };
};

// One-line form for confirms and banners: "WO 138447 (Day Kitchen)",
// "Warren Doors (custom job)".
export const jobText = (workOrder) => {
  const { title, detail, custom } = jobLabel(workOrder);
  if (custom) return `${title} (custom job)`;
  return detail ? `${title} (${detail})` : title;
};

// ---- Per-shop endpoints ----

/** Every shop clock endpoint, bound to one shop's API base ("/api/pcw"). */
export const createShopApi = (base) => {
  const url = (path, params) => `${base}${path}${queryString(params)}`;
  const send = (method, path, userId, body) =>
    adminRequest(url(path), userId, {
      method,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  const id = (value) => encodeURIComponent(value);

  return {
    // ---- Hours ----

    // Grouped totals (by work order / worker / category) plus whoever is
    // punched in right now. from/to are ISO datetimes; workOrder (a job key)
    // scopes everything to one job for the drill-down view, which then also
    // carries the job's allottedMinutes, dueDate, and assignments.
    fetchSummary: (userId, { from, to, workOrder } = {}) =>
      adminRequest(url("/summary", { from, to, workOrder }), userId),

    // filters.workOrder: a job key matches exactly; other text searches job
    // and customer names.
    fetchPunches: (userId, filters = {}) =>
      adminRequest(url("/punches", filters), userId),

    // Manual punch: { workerId, categoryId?, worksOrderNumber |
    // customWorkOrderId, punchInAt, punchOutAt } — categoryId blank/"none" is
    // the tablet's "None".
    createPunch: (punch, userId) => send("POST", "/punches", userId, punch),

    // Fix a punch: { workerId?, categoryId?, punchInAt?, punchOutAt? } —
    // setting a punch out time on an open punch closes it.
    updatePunch: (punchId, updates, userId) =>
      send("PUT", `/punches/${id(punchId)}`, userId, updates),

    closePunch: (punchId, userId) =>
      send("PUT", `/punches/${id(punchId)}/close`, userId),

    // ---- Workers ----

    fetchWorkers: (userId) => adminRequest(url("/workers"), userId),
    createWorker: (worker, userId) => send("POST", "/workers", userId, worker),
    // updates: { name?, passcode?, active?, resetFace? }
    updateWorker: (workerId, updates, userId) =>
      send("PUT", `/workers/${id(workerId)}`, userId, updates),

    // ---- Categories ----

    fetchCategories: (userId) => adminRequest(url("/categories"), userId),
    createCategory: (category, userId) =>
      send("POST", "/categories", userId, category),
    // updates: { name?, active?, sortOrder? }
    updateCategory: (categoryId, updates, userId) =>
      send("PUT", `/categories/${id(categoryId)}`, userId, updates),

    // ---- Open jobs & allotted time ----

    // Every active job — Bistrack's open work orders and unfinished custom
    // jobs — with all-time minutes (open punches included), workingNow,
    // lastActivity, allottedMinutes, dueDate, and Bistrack's dateRequired.
    // { source: "bistrack" | "recent", jobs }.
    fetchOpenJobs: (userId) => adminRequest(url("/open-jobs"), userId),

    // Budget a job by its key; allottedMinutes null clears it. The shop floor
    // TV's Job Hours screen shows progress against it.
    setAllottedTime: (jobKey, allottedMinutes, userId) =>
      send("PUT", `/allotments/${id(jobKey)}`, userId, { allottedMinutes }),

    // ---- Custom jobs ----

    // Created on the shop tablet; each row carries its all-time minutes,
    // punch count, workingNow (open punches), and lastActivity.
    fetchCustomJobs: (userId) =>
      adminRequest(url("/custom-work-orders"), userId),

    // updates: { name?, active? } — a rename also renames the job on its past
    // punches; active: false marks it finished.
    updateCustomJob: (jobId, updates, userId) =>
      send("PUT", `/custom-work-orders/${id(jobId)}`, userId, updates),

    // ---- Schedule ----

    // { today, from, to, source, jobs (every active job, with dueDate and
    // dateRequired), assignments (overlapping from..to, each with
    // actualMinutes) }. from/to are "YYYY-MM-DD" shop days.
    fetchSchedule: (userId, { from, to } = {}) =>
      adminRequest(url("/schedule", { from, to }), userId),

    // dueDate "YYYY-MM-DD", or null to clear it.
    setDueDate: (jobKey, dueDate, userId) =>
      send("PUT", `/deadlines/${id(jobKey)}`, userId, { dueDate }),

    // { workerId, jobKey, startDate, endDate, plannedMinutes?, note? }
    createAssignment: (assignment, userId) =>
      send("POST", "/assignments", userId, assignment),
    updateAssignment: (assignmentId, updates, userId) =>
      send("PUT", `/assignments/${id(assignmentId)}`, userId, updates),
    deleteAssignment: (assignmentId, userId) =>
      send("DELETE", `/assignments/${id(assignmentId)}`, userId),
  };
};
